/**
 * Content Composer domain types
 *
 * These types are copied from lab/domain.ts to maintain independence
 * between Content Composer and Creator Lab features.
 * When Creator Lab is removed, these types will remain.
 */

import { EditorTabs } from '../../domain';

export const CONTENT_TYPES = ['SOCIAL_POST', 'BLOG_POST', 'EMAIL'] as const;
export type ContentType = (typeof CONTENT_TYPES)[number];

export type ContentRepurposingStatus =
  | 'INITIAL'
  | 'IN_PROGRESS'
  | 'FINISHED'
  | 'FAILED';

/**
 * Pusher payload status (lowercase) as received from the backend.
 * Different from ContentRepurposingStatus which uses UPPERCASE.
 * Note: Backend sends 'completed' for successful generation (not 'finished').
 */
export type PusherContentGenerationStatus =
  | 'initial'
  | 'in_progress'
  | 'completed'
  | 'failed';

/**
 * Pusher payload for content_generation_completed event.
 * Used by useContentComposerPusherUpdates hook.
 */
export type ContentGenerationPusherPayload = {
  recordingId: string;
  contentId: string;
  contentType: ContentType;
  jobId: string;
  status: PusherContentGenerationStatus;
};

export type ContentRepurposingEligibility =
  | 'PENDING_CHECKS'
  | 'ELIGIBLE'
  | 'INELIGIBLE';

export type ContentRepurposingTopic = {
  id: string;
  topic: string;
};

export type ContentRepurposingTopics = {
  status: ContentRepurposingStatus;
  value: ContentRepurposingTopic[] | null;
};

export type ContentRepurposingToneOfVoice = {
  status: ContentRepurposingStatus;
  value: string | null;
};

export type ToneOfVoiceOption = {
  key: string;
  toneOfVoice: string;
  description: string;
};

export const DEFAULT_TONE_OF_VOICE_ID = 'professional_and_informative';

export type ToneOfVoiceOptionsResponse = {
  toneOfVoiceOptions: ToneOfVoiceOption[];
};

export type ContentRepurposingResponse =
  | {
      eligibility: Extract<
        ContentRepurposingEligibility,
        'INELIGIBLE' | 'PENDING_CHECKS'
      >;
    }
  | {
      eligibility: Extract<ContentRepurposingEligibility, 'ELIGIBLE'>;
      topics: ContentRepurposingTopics;
      toneOfVoice: ContentRepurposingToneOfVoice | null;
    };

/**
 * Email type option for email audience configuration
 */
export type EmailTypeOption = {
  key: string;
  labelKey: string;
};

/**
 * Email audience member with associated email types
 */
export type EmailAudienceMember = {
  key: string;
  labelKey: string;
  types: EmailTypeOption[];
};

/**
 * Email recipients with types response from API
 */
export type EmailRecipientsWithTypesResponse = {
  emailAudience: EmailAudienceMember[];
};

// ============================================================================
// Content History Types
// ============================================================================

/**
 * Content history item
 *
 * Note: Content is NOT included here - it's fetched separately by ID
 * and stored in generatedContent cache.
 */
export type ContentHistoryItem = {
  id: string;
  status: ContentRepurposingStatus;
  createdAt: string;
  contentType: ContentType;
  topics: string[];
  toneOfVoice: string;
  context?: {
    recipientTypeId: string;
    emailTypeId: string;
  };
};

/**
 * Content history API response with cursor-based pagination
 */
export type ApiContentHistoryResponse = {
  cursor: string | null;
  data: ContentHistoryItem[];
};

/**
 * Content history response used by Content Composer
 * Wraps API response with total count for UI display
 */
export type ContentHistoryResponse = {
  cursor: string | null;
  data: ContentHistoryItem[];
  total: number;
};

// ============================================================================
// Generated Content Types (Fetch by ID)
// ============================================================================

/**
 * API response when fetching generated content by ID
 * Content is an object with options array (for social posts with multiple options)
 */
export type ApiGeneratedContentResponse = {
  id: string;
  status: ContentRepurposingStatus;
  contentType: ContentType;
  topics: string[];
  toneOfVoice: string | null;
  context?: Record<string, unknown>;
  content: { options: string[] } | null;
  error?: string;
};

export type ApiContentGenerationRequest = {
  topics: string[]; // Required, min 1 item
  toneOfVoice: string; // Required, tone of voice external ID
  contentType: ContentType; // Required
  context?: Record<string, string>; // Optional, e.g., { recipientTypeId: '...', emailTypeId: '...' }
};

/**
 * Generated content cache entry fetch status
 * Tracks the client-side fetch state (separate from API generation status)
 */
export type GeneratedContentFetchStatus =
  | 'idle'
  | 'loading'
  | 'fetched'
  | 'failed';

/**
 * Generated content cache entry
 * Stores fetched content for a specific content ID
 *
 * Note: The API generation status (INITIAL, IN_PROGRESS, FINISHED, FAILED)
 * is stored on the history item, not here. This entry only tracks:
 * - fetchStatus: Client-side fetch state
 * - content: The actual generated text options
 * - error: Any fetch error message
 */
export type GeneratedContentEntry = {
  fetchStatus: GeneratedContentFetchStatus;
  content: { options: string[] } | null;
  error?: string;
};

/**
 * Generated content cache - maps content ID to its entry
 */
export type GeneratedContentCache = Record<string, GeneratedContentEntry>;

export const DEFAULT_GENERATED_CONTENT_ENTRY: GeneratedContentEntry = {
  fetchStatus: 'idle',
  content: null,
};

export const editorTabToContentType: Partial<Record<EditorTabs, ContentType>> =
  {
    'content-social-post': 'SOCIAL_POST',
    'content-blog-post': 'BLOG_POST',
    'content-email': 'EMAIL',
  };

export const contentTypeToEditorTab: Record<ContentType, EditorTabs> = {
  SOCIAL_POST: 'content-social-post',
  BLOG_POST: 'content-blog-post',
  EMAIL: 'content-email',
};
